import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Container,
  Paper,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import { Link as RouterLink, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { ThunkDispatch } from "@reduxjs/toolkit";

import PageTitle from "../../components/Page Component/PageTitle";
import { RootState } from "../../store/Store";
import UtilizationChart from "../../components/jira/utilization/UtilizationChart";
import BugsAssigneeChart from "../../components/jira/bugsAssignee/BugsAssigneeChart";
import BugsReportChart from "../../components/jira/bugs/BugsReportChart";
import BreachDueDatesReportChart from "../../components/jira/breachDueDates/BreachDueDatesReportChart";
import NonComplianceChart from "../../components/jira/nonCompliance/NonComplianceChart";
import {
  fetchData,
  FetchDataFailureAction,
  FetchDataSuccessAction,
} from "../../store/actions";
import { viewUsers } from "../../Service/Apis";

const VALID_MONTHS = new Set(["1", "3", "6", "12"]);

const EmployeeJiraAnalyticsPage: React.FC = () => {
  const { employeeId } = useParams<{ employeeId: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const selectedRole = useSelector((s: RootState) => s.auth.selectedRole);
  const dispatch2 = useDispatch<
    ThunkDispatch<RootState, null, FetchDataSuccessAction | FetchDataFailureAction>
  >();

  const initialMonths = searchParams.get("months") || "1";
  const [months, setMonths] = useState(
    VALID_MONTHS.has(initialMonths) ? initialMonths : "1"
  );
  const [employeeDisplayName, setEmployeeDisplayName] = useState("");

  const canAccess = selectedRole === "Admin" || selectedRole === "HR";

  useEffect(() => {
    if (!canAccess) {
      navigate("/landing", { replace: true });
    }
  }, [canAccess, navigate]);

  useEffect(() => {
    if (!employeeId) return;
    let cancelled = false;
    (async () => {
      try {
        const rows = await dispatch2(fetchData(viewUsers));
        if (cancelled || !Array.isArray(rows)) return;
        const row = rows.find((u: { id?: string }) => String(u.id) === String(employeeId));
        if (row) {
          const u = row as { first_name?: string; last_name?: string; name?: string };
          const fromParts = `${u.first_name ?? ""} ${u.last_name ?? ""}`.trim();
          setEmployeeDisplayName(fromParts || u.name || "");
        }
      } catch {
        /* keep empty */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [dispatch2, employeeId]);

  const scopedSeriesName = useMemo(
    () => employeeDisplayName.trim(),
    [employeeDisplayName]
  );

  if (!canAccess) {
    return null;
  }

  if (!employeeId) {
    return (
      <Container sx={{ mt: 12 }}>
        <Typography color="error">Missing employee id.</Typography>
        <Button component={RouterLink} to="/employeeinformation" sx={{ mt: 2 }}>
          Back to employees
        </Button>
      </Container>
    );
  }

  const pageHeading = scopedSeriesName || "";

  return (
    <Container maxWidth="xl" sx={{ mt: 12, mb: 10 }}>
      <Button component={RouterLink} to="/employeeinformation" sx={{ mb: 2 }}>
        Back to employee information
      </Button>
      <Box sx={{ mb: 2 }}>
        <PageTitle>{pageHeading}</PageTitle>
      </Box>

      <Paper sx={{ p: 2, mb: 3 }}>
        <Typography variant="subtitle1" gutterBottom>
          Period
        </Typography>
        <ToggleButtonGroup
          size="medium"
          value={months}
          exclusive
          onChange={(_, v) => v != null && setMonths(v)}
        >
          <ToggleButton value="1" sx={{ px: 2.5, py: 1, fontSize: "0.95rem" }}>
            1M
          </ToggleButton>
          <ToggleButton value="3" sx={{ px: 2.5, py: 1, fontSize: "0.95rem" }}>
            3M
          </ToggleButton>
          <ToggleButton value="6" sx={{ px: 2.5, py: 1, fontSize: "0.95rem" }}>
            6M
          </ToggleButton>
          <ToggleButton value="12" sx={{ px: 2.5, py: 1, fontSize: "0.95rem" }}>
            1Y
          </ToggleButton>
        </ToggleButtonGroup>
      </Paper>

      <UtilizationChart
        employeeId={employeeId}
        controlledMonth={months}
        hideMonthButtons
        projectLinesForEmployee
        hideSelectAllEmployeesSwitch
      />
      <BugsAssigneeChart
        employeeId={employeeId}
        controlledMonth={months}
        hideMonthButtons
        tooltipScopedSeriesName={scopedSeriesName || undefined}
      />
      <BugsReportChart
        employeeId={employeeId}
        controlledMonth={months}
        hideMonthButtons
        tooltipScopedSeriesName={scopedSeriesName || undefined}
      />
      <BreachDueDatesReportChart
        employeeId={employeeId}
        controlledMonth={months}
        hideMonthButtons
        tooltipScopedSeriesName={scopedSeriesName || undefined}
      />
      <NonComplianceChart
        employeeId={employeeId}
        controlledMonth={months}
        hideMonthButtons
        tooltipScopedSeriesName={scopedSeriesName || undefined}
      />
    </Container>
  );
};

export default EmployeeJiraAnalyticsPage;
